/**
 * Botão / status da sidebar — a decisão do que mostrar vive em
 * `domain/button-state.ts` (GUPM-261); aqui é só tradução em JSX.
 *
 *   - Sem plantão ativo → operador vê "Iniciar plantão" (bootstrap, caminho
 *     excepcional); admin/RT veem AVISO destacado (o banco os rejeita como
 *     plantonistas, e a ausência de plantonista é anomalia).
 *   - Holder atual → "Iniciar troca de plantão".
 *   - Plantão de outro → informativo; ADMIN e RT ganham "Encerrar plantão"
 *     (destrave de plantão esquecido, com motivo + TOTP de quem destrava).
 *   - Consulta falhou → estado explícito de falha, NUNCA "Iniciar plantão".
 *
 * O holder vem de `get_current_shift` (RPC security definer): visível a
 * qualquer perfil — a RLS de SELECT da tabela esconderia a troca de um operador
 * que não participou, e era isso que fazia o botão oferecer "Iniciar plantão"
 * com plantão alheio ativo. O plantão é GLOBAL (cobre A, B e C): a consulta não
 * leva barragem. A cadeia é garantida no BANCO pelo trigger
 * shift_handover_validate_chain, não por esta UI.
 */

import { useState } from 'react';
import { useCloseShift, useCurrentEngineer, useCurrentShift } from './hooks';
import { shiftButtonState } from './domain/button-state';
import { useDamId } from './useDamId';
import ShiftHandoverModal from './ShiftHandoverModal';
import EncerrarPlantaoModal from './EncerrarPlantaoModal';

function formatHandoverTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hhmm = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return hhmm;
  const ddmm = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${ddmm} ${hhmm}`;
}

export default function ShiftHandoverButton() {
  // `damId` só é necessário para ABRIR o modal de troca (o INSERT precisa de
  // uma barragem de registro). O estado do botão não depende dele — o plantão
  // é global.
  const damId = useDamId();
  const { data: engineer } = useCurrentEngineer();
  const currentShift = useCurrentShift();
  const [open, setOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const closeShift = useCloseShift();

  const containerBase = 'mx-2 mb-2 rounded-lg border px-3 py-2 text-xs font-bold transition text-center';

  if (!engineer || currentShift.isPending) {
    return (
      <div className={`${containerBase} border-jf-border bg-jf-surface text-jf-dim`}>
        Carregando…
      </div>
    );
  }

  const state = shiftButtonState({
    profile: engineer.profile,
    userId: engineer.id,
    current: currentShift.data ?? null,
    queryFailed: currentShift.isError,
    // O relógio entra aqui, na borda: o domínio recebe o instante e não o lê.
    // Basta a precisão do render — o limiar é de 12 h, e a query já revalida a
    // cada navegação.
    now: new Date(),
  });

  return (
    <>
      {state.kind === 'falha-consulta' && (
        <div
          title="Não foi possível consultar o plantão ativo. Não inicie plantão sem confirmar com a equipe."
          className={`${containerBase} border-amber-500/50 bg-amber-500/10 text-amber-300 cursor-default`}
        >
          <span className="block">⚠ Plantão indisponível</span>
          <span className="block text-[10px] mt-0.5 font-normal text-amber-200/80">
            falha ao consultar — tente recarregar
          </span>
        </div>
      )}

      {state.kind === 'iniciar' && (
        <button
          onClick={() => setOpen(true)}
          // Mesma razão do botão de troca: `useDamId` devolve `data ?? undefined`
          // e não separa "carregando" de "falhou", então travar aqui deixava um
          // botão mudo — justamente no caminho que este card existe para
          // destravar (sala vazia, operador precisa assumir). A abertura precisa
          // de barragem de verdade (grava linha), e é o rodapé do formulário que
          // diz isso, com todas as letras.
          aria-label="Abrir plantão"
          className={`${containerBase} border-jf-yellow/30 bg-jf-yellow/10 text-jf-yellow hover:bg-jf-yellow/20`}
        >
          {/* "Abrir" e não "Iniciar": quem clica assume o próprio plantão. O
              rótulo antigo descrevia um formulário que nomeava outra pessoa. */}
          <span className="block">📋 Abrir plantão</span>
          <span className="block text-[10px] mt-0.5 font-normal text-amber-200/80">
            ⚠ ninguém de plantão agora
          </span>
        </button>
      )}

      {state.kind === 'sem-plantao-alerta' && (
        <div
          title="Nenhum operador está de plantão. Em regime normal sempre deve haver um plantonista."
          className={`${containerBase} border-amber-500/50 bg-amber-500/10 text-amber-300 cursor-default`}
        >
          <span className="block">⚠ Nenhum plantonista ativo</span>
          <span className="block text-[10px] mt-0.5 font-normal text-amber-200/80">
            aguardando um operador iniciar
          </span>
        </div>
      )}

      {state.kind === 'iniciar-troca' && (
        // UM botão só. A Rev.1 pendurou um segundo ("Encerrar meu plantão")
        // dentro deste bloco, e além de feio separava as duas saídas do mesmo
        // turno em lugares diferentes — uma com checklist, outra sem. Agora as
        // duas passam pelo formulário: lá dentro se escolhe se alguém rende.
        <button
          onClick={() => setOpen(true)}
          // SEM `disabled={!damId}`: o plantão é GLOBAL (cobre A, B e C) e
          // encerrar não usa barragem nenhuma — a RPC só recebe motivo e
          // checklist. Travar a porta inteira na resolução da barragem tirava do
          // plantonista a única saída por causa de um dado que a operação não
          // usa. A troca, essa sim, precisa: o bloqueio dela mora no rodapé do
          // formulário, dizendo por quê.
          aria-label="Trocar de plantão"
          // Âmbar acima de 12 h: o mesmo botão, com a urgência que o turno já
          // tem. Cor nova em vez de aviso extra porque o plantonista olha para
          // este canto o turno inteiro — o que muda tem que saltar sem exigir
          // leitura.
          className={`${containerBase} ${
            state.staleHours !== null
              ? 'border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
              : 'border-jf-yellow/30 bg-jf-yellow/10 text-jf-yellow hover:bg-jf-yellow/20'
          }`}
        >
          <span className="block">📋 Trocar de plantão</span>
          {state.staleHours !== null ? (
            <span className="block text-[10px] mt-0.5 font-normal text-amber-200/80">
              {`⚠ sem passagem há ${state.staleHours}h`}
            </span>
          ) : (
            <span className="block text-[10px] mt-0.5 font-normal text-green-300/80">
              {`🟢 Plantão ativo desde ${formatHandoverTime(state.since)}`}
            </span>
          )}
          {/* SEM aviso de "seu cadastro está inativo" aqui: este ramo exige
              `engineer.id === current.operatorInId`, e `engineer` vem de
              `app_users_public`, view que filtra `is_active`. Um plantonista
              desligado não resolve `engineer`, cai no early-return de
              carregamento e nunca chega até aqui — o aviso seria código morto
              fingindo cobrir o caso. Quem vê o estado dele é quem observa, no
              ramo `plantao-alheio`. O que o próprio desligado enxerga hoje é
              "Carregando…" para sempre: buraco anterior a este card, em
              GUPM-287. */}
        </button>
      )}

      {state.kind === 'plantao-alheio' && (
        <div
          title={
            state.canEnd
              ? undefined
              : `Apenas ${state.holderName} pode iniciar a próxima troca de plantão.`
          }
          className={`${containerBase} cursor-default ${
            state.staleHours !== null || !state.holderIsActive
              ? 'border-amber-500/50 bg-amber-500/10 text-amber-300'
              : 'border-jf-border bg-jf-surface text-jf-dim'
          }`}
        >
          <span className="block">{`🔒 Plantão atual: ${state.holderName}`}</span>
          <span
            className={`block text-[10px] mt-0.5 font-normal ${
              state.staleHours !== null ? 'text-amber-200/80' : 'text-jf-muted'
            }`}
          >
            {state.staleHours !== null
              ? `⚠ sem passagem há ${state.staleHours}h`
              : `desde ${formatHandoverTime(state.since)}`}
          </span>
          {/* Plantonista desligado no cadastro: ninguém passa e ninguém assume
              até um admin destravar. Sem esta linha a operação fica parada sem
              nada na tela explicando por quê. */}
          {!state.holderIsActive && (
            <span className="block text-[10px] mt-0.5 font-normal text-red-300">
              ⚠ plantonista inativo — precisa destrave
            </span>
          )}
          {state.canEnd && (
            <button
              onClick={() => {
                closeShift.reset();
                setEndOpen(true);
              }}
              aria-label="Encerrar plantão"
              className="mt-1.5 w-full rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-[11px] font-bold text-red-300 hover:bg-red-500/20"
            >
              ⏹ Encerrar plantão
            </button>
          )}
        </div>
      )}

      {/* Sem `&& damId`: o formulário abre mesmo sem barragem resolvida, porque
          o encerramento não depende dela. Quem depende é a troca/abertura, e é
          o próprio formulário que barra — com explicação. */}
      {open && (
        <ShiftHandoverModal
          isOpen={open}
          onClose={() => setOpen(false)}
          currentEngineer={engineer}
          damId={damId}
          mode={state.kind === 'iniciar' ? 'abertura' : 'troca'}
        />
      )}

      {/* Este modal ficou só para o DESTRAVE (admin ou RT — GUPM-313): quem
          destrava não viveu o turno e não tem checklist a atestar. O
          auto-encerramento migrou para o formulário de troca (opção
          "— Ninguém —"). GUPM-272 Rev.2. */}
      {endOpen && state.kind === 'plantao-alheio' && (
        <EncerrarPlantaoModal
          isOpen={endOpen}
          holderName={state.holderName}
          isPending={closeShift.isPending}
          error={closeShift.error?.message ?? null}
          onClose={() => setEndOpen(false)}
          onConfirm={(reason, code) =>
            closeShift.mutate(
              { reason, code },
              {
                onSuccess: () => setEndOpen(false),
              },
            )
          }
        />
      )}
    </>
  );
}
